import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ParkingLotImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  onUploadComplete: (imageId: string) => void;
}

const MIN_WIDTH = 3000;
const MIN_HEIGHT = 2000;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

export function ParkingLotImageUploadDialog({
  open,
  onOpenChange,
  shopId,
  onUploadComplete,
}: ParkingLotImageUploadDialogProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { toast } = useToast();

  const validateFile = useCallback((file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      // Check file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        reject('Please upload a JPG or PNG image.');
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        reject('File size must be less than 50MB.');
        return;
      }

      // Check image dimensions
      const img = new Image();
      img.onload = () => {
        if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
          reject(`Image must be at least ${MIN_WIDTH}×${MIN_HEIGHT} pixels. Your image is ${img.width}×${img.height}.`);
        } else {
          resolve({ width: img.width, height: img.height });
        }
      };
      img.onerror = () => reject('Failed to load image.');
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    try {
      const dims = await validateFile(file);
      setFile(file);
      setDimensions(dims);
      setPreview(URL.createObjectURL(file));
    } catch (err) {
      setError(err as string);
      setFile(null);
      setPreview(null);
      setDimensions(null);
    }
  }, [validateFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  }, [handleFile]);

  const handleUpload = async () => {
    if (!file || !dimensions) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate unique filename
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const uuid = crypto.randomUUID();
      const storagePath = `${shopId}/${uuid}.${ext}`;

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 80));
      }, 200);

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('parking-lot-images')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      clearInterval(progressInterval);

      if (uploadError) throw uploadError;

      setUploadProgress(90);

      // Create database record
      const { data: imageRecord, error: dbError } = await supabase
        .from('parking_lot_images')
        .insert({
          shop_id: shopId,
          image_path: storagePath,
          image_width: dimensions.width,
          image_height: dimensions.height,
          status: 'PENDING',
        })
        .select('id')
        .single();

      if (dbError) throw dbError;

      setUploadProgress(100);

      toast({
        title: 'Image uploaded',
        description: 'Now calibrate the image to set the scale.',
      });

      onUploadComplete(imageRecord.id);
      handleReset();
      onOpenChange(false);

    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image.');
      toast({
        title: 'Upload failed',
        description: err.message || 'Failed to upload image.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setDimensions(null);
    setError(null);
    setUploadProgress(0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Parking Lot Image</DialogTitle>
          <DialogDescription>
            Upload a drone or overhead photo of your parking lot. 
            The image will be used as the base for your parking spot layout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!file ? (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
                error && 'border-destructive'
              )}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm font-medium">
                Drag and drop your image here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                JPG or PNG, minimum {MIN_WIDTH}×{MIN_HEIGHT}px, max 50MB
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-muted aspect-video">
                <img
                  src={preview!}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={handleReset}
                  disabled={isUploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
                <span className="truncate flex-1">{file.name}</span>
                {dimensions && (
                  <span className="text-xs">
                    {dimensions.width} × {dimensions.height}
                  </span>
                )}
              </div>

              {isUploading && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} />
                  <p className="text-xs text-center text-muted-foreground">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file || isUploading}>
            {isUploading ? 'Uploading...' : 'Upload & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
